import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useState } from 'react';

import { useLogger } from '@/hooks/useLogger';
import { ChatAttachment } from '@/models/GroupChatMessage';
import { generatePreSignedUrls } from '@/services/api/files';

const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
const MAX_ATTACHMENTS = 5;

export interface ChatFileValidationError {
  type: 'invalid_type' | 'too_large' | 'too_many';
  messageKey: string;
  messageParams?: Record<string, unknown>;
}

export const useChatFileUpload = (groupId: string) => {
  const logger = useLogger('useChatFileUpload');

  const [uploadProgress, setUploadProgress] = useState(0);
  const [inProgress, setInProgress] = useState(false);

  const { mutateAsync: generatePreSignedUrlsAsync } = useMutation({
    mutationFn: (requestBody: {
      files: { filename: string; mimeType: string; isExtractable: boolean }[];
      groupId: string;
    }) => generatePreSignedUrls(requestBody),
  });

  const validateFiles = useCallback((files: File[]): ChatFileValidationError | null => {
    if (files.length > MAX_ATTACHMENTS) {
      return {
        type: 'too_many',
        messageKey: 'file.max_attachments',
        messageParams: { max: MAX_ATTACHMENTS },
      };
    }
    for (const file of files) {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        return { type: 'invalid_type', messageKey: 'file.invalid_type' };
      }
      if (file.size === 0) {
        return { type: 'too_large', messageKey: 'file.empty' };
      }
      if (file.size > MAX_FILE_SIZE) {
        return { type: 'too_large', messageKey: 'file.too_large' };
      }
    }
    return null;
  }, []);

  const startUpload = useCallback(
    async (files: File[]): Promise<ChatAttachment[] | null> => {
      setUploadProgress(0);
      setInProgress(true);

      try {
        logger.debug('generating presigned urls for chat files:', { fileCount: files.length });
        const preSignedUrls = await generatePreSignedUrlsAsync({
          files: files.map((file) => ({
            filename: file.name,
            mimeType: file.type,
            isExtractable: false,
            fileSize: file.size,
          })),
          groupId,
        });

        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        // Track per-file loaded bytes to avoid over-counting from repeated progress events
        const loadedPerFile = new Map<number, number>();

        const uploadPromises = preSignedUrls.map(async (presigned, index) => {
          const file = files[index];
          if (!file) return;
          loadedPerFile.set(index, 0);

          await axios.put(presigned.preSignedUrl, file, {
            headers: { 'Content-Type': presigned.mimeType },
            onUploadProgress: (progressEvent) => {
              loadedPerFile.set(index, progressEvent.loaded);
              const totalLoaded = Array.from(loadedPerFile.values()).reduce((a, b) => a + b, 0);
              const overallPercent = Math.min(99, Math.round((totalLoaded / totalSize) * 100));
              setUploadProgress(overallPercent);
            },
          });
        });
        await Promise.all(uploadPromises);

        const attachments: ChatAttachment[] = preSignedUrls.map(
          ({ filename, mimeType, key }, index) => ({
            s3Key: key,
            filename,
            fileSize: files[index]?.size ?? 0,
            mimeType,
          }),
        );

        setUploadProgress(100);
        setInProgress(false);
        logger.debug('chat file upload complete:', { attachmentCount: attachments.length });
        return attachments;
      } catch (error) {
        logger.error('chat file upload failed', { error });
        setInProgress(false);
        return null;
      }
    },
    [groupId, generatePreSignedUrlsAsync, logger],
  );

  const clearUploadProgress = useCallback(() => {
    setUploadProgress(0);
  }, []);

  return {
    startUpload,
    validateFiles,
    uploadProgress,
    inProgress,
    clearUploadProgress,
  };
};
